import React, { ReactElement } from 'react';

import { Inject } from '@odinjs/odin';

import { AutocompleteAPI, ContextualBarAPI, FetchAPI, InputAPI, Link, Panel, PanelAPI } from '@philips-emr/tws-components';
import { Size } from '@philips-emr/tws-constants';
import Controller, { OnAfterFetch, OnChange, OnClick, OnDynamicContentRender, OnMount } from '@philips-emr/tws-core/lib/client';
import { ChangeEvent, FetchEvent, MountEvent, RenderEvent } from '@philips-emr/tws-core/lib/component/types';
import { generateUUID, isEmptyArrayOrObject } from '@philips-emr/tws-utils';

import { ReactComponent as MagnifierIcon } from '../../../resources/assets/search.svg';
import { FragmentNames } from '../../codegen';
import { ContractDetailsAliases as Aliases } from '../../codegen/constants/aliases/contract-details-aliases';
import BaseController from '../BaseController';
import { chooseMargin, ContractualDraft } from '../negotiation/negotiation-contractual-draft-controller';

@Controller({ fragment: FragmentNames.CONTRACT_DETAILS })
class ContractDetailsController extends BaseController {

  @Inject(Aliases.FETCH_CLAUSE)
  private fetchClause: FetchAPI;
  @Inject(Aliases.FETCH_CLAUSES)
  private fetchClauses: FetchAPI;
  @Inject(Aliases.CONTEXTUAL_BAR_CLAUSE)
  private contextualBarClause: ContextualBarAPI;
  @Inject(Aliases.PANEL_CLAUSE)
  private panelClause: PanelAPI;
  @Inject(Aliases.PANEL_LOADER)
  private panelLoader: PanelAPI;
  @Inject(Aliases.PANEL_ATTACHMENTS)
  private panelAttachments: PanelAPI;
  @Inject(Aliases.AUTOCOMPLETE_MENU_MOBILE)
  private autocompleteMenuMobile: AutocompleteAPI;

  @OnMount([Aliases.FETCH_CLAUSES, Aliases.FETCH_MENU_MOBILE])
  onMountFetchClauses(event: MountEvent): void {
    const api = event.target as FetchAPI;
    const { nrSeqContract } = this.historyService.getCurrentRouteParams();

    if (api.getAlias() == Aliases.FETCH_MENU_MOBILE) {
      api.setParameters({ nrSeqContract, mobile: true });
      api.forceFetch();
      return;
    }
    api.setParameters({ nrSeqContract });
    api.forceFetch();
  }

  @OnChange(Aliases.AUTOCOMPLETE_MENU_MOBILE)
  onChange(event: ChangeEvent<ContractualDraft>): void {
    const { nrSeqContractClause, draftTitle, attachmentsMenu } = event.value;
    if (attachmentsMenu) {
      this.panelClause.setVisible(false);
      this.panelAttachments.setVisible(true);
      return;
    }
    this.forceFetchClause(nrSeqContractClause, draftTitle);
  }

  @OnAfterFetch(Aliases.FETCH_MENU_MOBILE)
  onAfterFetchMobile(event: FetchEvent): void {
    if (event.data && !isEmptyArrayOrObject(event.data)) {
      const data = event.data as [{}];
      this.autocompleteMenuMobile.setValue(data[0]);
    }
  }

  @OnMount(Aliases.INPUT_FILTER)
  onMountFilter(event: MountEvent): void {
    const api = event.target as InputAPI;
    api.setExternalAction(<MagnifierIcon className="c-module-selector__search-icon" />);
  }

  @OnDynamicContentRender(Aliases.FIELD_CLAUSES)
  onRenderClauses(event: RenderEvent): void {
    const { data, render } = event;
    if (!isEmptyArrayOrObject(data)) {
      const { draftTitle, nrSeqContractClause, secondLevel, thirdLevel, fourthLevel } = data as ContractualDraft;
      const margin = chooseMargin(secondLevel, thirdLevel, fourthLevel);
      render((): ReactElement =>
        <Panel className="" uuid={generateUUID()} margin={margin}>
          <Link value={draftTitle} onClick={() => this.forceFetchClause(nrSeqContractClause, draftTitle)} size={Size.MEDIUM} />
        </Panel>);
    }
  }

  @OnClick(Aliases.LINK_ATTACHMENT)
  onClickAttachment(): void {
    this.panelClause.setVisible(false);
    this.panelAttachments.setVisible(true);
  }

  @OnChange(Aliases.INPUT_FILTER)
  onEditFilter(event: ChangeEvent<ContractualDraft>): void {
    const params = this.fetchClauses.getParameters();
    const draftTitle = event.value;
    this.fetchClauses.setParameters({ ...params, draftTitle });
    this.fetchClauses.forceFetch();
  }

  private forceFetchClause(nrSeqContractClause: number, draftTitle: string): void {
    const visible = this.panelClause.isVisible();
    this.panelLoader.setVisible(!visible);
    this.panelClause.setVisible(true);
    this.panelAttachments.setVisible(false);
    this.context.set({ nrSeqContractClause, draftTitle });
    setTimeout(() => {
      const { nrSeqContract } = this.historyService.getCurrentRouteParams();
      this.fetchClause.setParameters({ nrSeqContract, nrSeqContractClause });
      this.fetchClause.forceFetch();
      this.contextualBarClause.setTitle(draftTitle);
      this.panelLoader.setVisible(false);
    }, visible ? 0 : 500);

  }
}

export default ContractDetailsController;
